import {WixSdk} from '@wix/js-sdk-wrapper';
import {inject, injectable, decorate} from 'inversify';
import {merge, Observable} from 'rxjs';
import {fromPromise} from 'rxjs/internal-compatibility';
import {distinctUntilChanged, filter, map, share, switchMap, withLatestFrom} from 'rxjs/operators';
import {services} from '../../../../ioc/services';
import {ICollectionFetcher} from '../../../../services/CollectionFetcher/ICollectionFetcher';
import {CollectionResponse, ICollectionManager} from '../../../../services/CollectionManager/ICollectionManager';
import {debug} from '../../../../utils/debug';
import {isDefined} from '../../../../utils/functions';
import {settingUpdate$} from '../../wixSdkObservables';
import {ICollectionObservable} from './ICollectionObservable';

export class CollectionObservable implements ICollectionObservable {
  constructor(
    private readonly collectionManager: ICollectionManager,
    private readonly collectionFetcher: ICollectionFetcher,
  ) {}

  public getCollection() {
    const newAlbum$ = this.newAlbumObservable();

    const newCollectionLoad$ = merge(fromPromise(this.collectionManager.getCollection()), newAlbum$).pipe(
      share(),
      debug('collection', 'collection first collection got'),
    );

    const collectionUpdate$ = settingUpdate$.pipe(
      withLatestFrom(newCollectionLoad$),
      filter(([{type, albumId}, currentCollection]) => {
        return type === 'albumModified' && currentCollection.collection?.id === albumId;
      }),
      switchMap(([data]) => {
        const albumId = data.albumId;
        if (!albumId) {
          throw new Error('AlbumId cannot be undefined in albumModified');
        }
        return this.loadCollection(albumId);
      }),
    );

    const collection$: Observable<CollectionResponse> = merge(newCollectionLoad$, collectionUpdate$).pipe(share());
    return collection$;
  }

  private newAlbumObservable(): Observable<CollectionResponse> {
    const observable: Observable<string> = settingUpdate$.pipe(
      filter(({type, compId}) => type === 'newAlbum' && compId === WixSdk.Utils.getCompId()),
      map(data => data.albumId),
      filter(isDefined),
    );

    const collectionUpdateRequestEvents$: Observable<CollectionResponse> = observable.pipe(
      distinctUntilChanged(),
      debug('collection', 'start load'),
      switchMap(albumId => this.loadCollection(albumId)),
      debug('collection', 'end load'),
    );
    return collectionUpdateRequestEvents$;
  }

  private loadCollection(albumId: string): Observable<CollectionResponse> {
    const metaSiteId = WixSdk.Utils.getInstanceValue('metaSiteId');

    return fromPromise(
      this.collectionFetcher.fetchCollection(albumId, metaSiteId).then(collection => ({
        from: 'settings-update',
        collection,
      })),
    );
  }
}

decorate(injectable(), CollectionObservable);
decorate(inject(services.collectionManager) as ParameterDecorator, CollectionObservable, 0);
decorate(inject(services.collectionFetcher) as ParameterDecorator, CollectionObservable, 1);
